/**
 * Per-browser preferences (language, theme). Storage can be unavailable (private mode, blocked
 * site data); the app then falls back to defaults and keeps working.
 */
export function readPreference(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    reportStorageProblem(error);
    return null;
  }
}

export function writePreference(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch (error) {
    reportStorageProblem(error);
  }
}

function reportStorageProblem(error: unknown): void {
  if (import.meta.env.DEV) console.warn('Preference storage unavailable', error);
}

export const PREFERENCE_KEYS = {
  locale: 'app.locale',
  theme: 'app.theme',
} as const;

/**
 * Per-tab working data (the batch page's batch, WP-1b.3). Lives for the browser tab only; every
 * read and write may fail (private mode, blocked storage), and then the data lives for the page only.
 */
export function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    reportStorageProblem(error);
    return null;
  }
}

export function writeSession(key: string, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch (error) {
    reportStorageProblem(error);
  }
}

/** Removes every per-tab entry whose key starts with `prefix` (sign-out). */
export function clearSessionPrefix(prefix: string): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    for (const key of keys) window.sessionStorage.removeItem(key);
  } catch (error) {
    reportStorageProblem(error);
  }
}

/** Prefix of the batch page's per-tab batches: `ctax.batch.v1.<orgId>.<userId>`. */
export const BATCH_SESSION_PREFIX = 'ctax.batch.';
